import React, { useCallback, useMemo, useRef, useState } from 'react';

import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Download as DownloadIcon } from '@mui/icons-material';
import { Box, IconButton, Paper, Typography } from '@mui/material';

import currencyFormatter from '~/utils/currencyFormatter';

import DateRangeSlider from './DateRangeSlider';
import RatePlanInfo from './RatePlanInfo';
import SupplierFilterChips from './SupplierFilterChips';
import { recomputeAnalysis } from './analyzeParityCsv';
import {
  BEST_COMPETITOR_CHART_COLOR,
  BEST_COMPETITOR_LABEL,
  CHART_LINE_COLORS,
  LE_CHART_COLOR,
  LE_SUPPLIER_LABEL,
  SECTION_SX,
} from './constants';
import { downloadDomElementAsPng } from './downloadDomElementAsPng';
import { RateStrategyPanelProps } from './rateStrategyPanel';
import { safeParityFilenameSlug } from './safeParityFilenameSlug';
import { ParityAnalysis, TRACKED_OTAS } from './types';
import { useParityReportDateRange } from './useParityReportDateRange';
import { buildChartData, computeYDomain, deriveActiveOtas } from './utils';

interface ChartSectionProps {
  analysis: ParityAnalysis;
  rateStrategy: RateStrategyPanelProps;
}

export default function ChartSection({ analysis, rateStrategy }: ChartSectionProps) {
  const { ratePlans, ratePlansLoading, ratePlansError, lpcPromotionalDiscounts, promotionsLoading, promotionsError } =
    rateStrategy;
  const fmtPrice = (n: number) => currencyFormatter(analysis.currency, n, 2);
  const fmtPriceRounded = (v: number) => currencyFormatter(analysis.currency, v, 0);

  const chartSectionRef = useRef<HTMLDivElement>(null);
  const [showLegend, setShowLegend] = useState(false);

  const [channelVisibility, setChannelVisibility] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const ota of TRACKED_OTAS) {
      init[ota] = true;
    }
    init[BEST_COMPETITOR_LABEL] = true;
    return init;
  });

  const totalDays = analysis.dailyDetail.length;
  const { dateRange, setDateRange, committedDateRange, setCommittedDateRange } = useParityReportDateRange(
    totalDays,
    analysis,
  );

  const handleRangeCommit = useCallback(
    (r: Array<number>) => {
      setDateRange(r);
      setCommittedDateRange(r);
    },
    [setDateRange, setCommittedDateRange],
  );

  const toggleChannel = (channel: string) => {
    setChannelVisibility((prev) => ({ ...prev, [channel]: !prev[channel] }));
  };

  const activeChartOtas = useMemo(() => deriveActiveOtas(channelVisibility), [channelVisibility]);
  const chartAnalysis = useMemo(() => recomputeAnalysis(analysis, activeChartOtas), [analysis, activeChartOtas]);
  const visibleDailyDetail = useMemo(
    () => chartAnalysis.dailyDetail.slice(committedDateRange[0], committedDateRange[1] + 1),
    [chartAnalysis.dailyDetail, committedDateRange],
  );
  const unifiedChartData = useMemo(
    () => buildChartData(visibleDailyDetail, chartAnalysis.leColumn),
    [visibleDailyDetail, chartAnalysis.leColumn],
  );
  const { yMin, yMax } = useMemo(
    () => computeYDomain(visibleDailyDetail, activeChartOtas),
    [visibleDailyDetail, activeChartOtas],
  );

  const handleDownloadChart = useCallback(async () => {
    if (!chartSectionRef.current) {
      return;
    }
    setShowLegend(true);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      await downloadDomElementAsPng(
        chartSectionRef.current,
        `price_comparison_${safeParityFilenameSlug(analysis.propertyName)}_${analysis.analysisDate}.png`,
      );
    } finally {
      setShowLegend(false);
    }
  }, [analysis.propertyName, analysis.analysisDate]);

  return (
    <Paper elevation={0} sx={SECTION_SX}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Price Comparison Analysis</Typography>
        <IconButton onClick={handleDownloadChart} title="Download chart as PNG">
          <DownloadIcon />
        </IconButton>
      </Box>

      <Box sx={{ mb: 3 }}>
        <SupplierFilterChips
          suppliers={TRACKED_OTAS}
          isActive={(ota) => channelVisibility[ota]}
          onToggle={toggleChannel}
          label="Show/Hide Channels"
          showLeChip
          showBestCompetitorChip
          isBestCompetitorActive={channelVisibility[BEST_COMPETITOR_LABEL]}
          onToggleBestCompetitor={() => toggleChannel(BEST_COMPETITOR_LABEL)}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box sx={{ flex: 1 }}>
          <Box ref={chartSectionRef}>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={unifiedChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis domain={[yMin, yMax]} tickFormatter={fmtPriceRounded} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => (v != null ? fmtPrice(Number(v)) : '—')} />
                {showLegend && <Legend />}
                <Line
                  type="monotone"
                  dataKey={chartAnalysis.leColumn}
                  name={LE_SUPPLIER_LABEL}
                  stroke={LE_CHART_COLOR}
                  strokeWidth={4}
                  dot={false}
                  connectNulls={false}
                />
                {channelVisibility[BEST_COMPETITOR_LABEL] && (
                  <Line
                    type="monotone"
                    dataKey={BEST_COMPETITOR_LABEL}
                    stroke={BEST_COMPETITOR_CHART_COLOR}
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                  />
                )}
                {TRACKED_OTAS.filter((ota) => channelVisibility[ota]).map((ota) => (
                  <Line
                    key={ota}
                    type="monotone"
                    dataKey={ota}
                    stroke={CHART_LINE_COLORS[ota]}
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {totalDays > 1 && (
            <Box data-html2canvas-ignore>
              <DateRangeSlider
                dates={analysis.dailyDetail.map((d) => d.date)}
                range={[dateRange[0], dateRange[1]]}
                onRangeChange={setDateRange}
                onRangeCommit={handleRangeCommit}
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{ width: 320, flexShrink: 0 }}
          {...(ratePlans.length === 0 && !ratePlansLoading && !ratePlansError
            ? { 'data-html2canvas-ignore': true }
            : {})}
        >
          <RatePlanInfo
            ratePlans={ratePlans}
            isLoading={ratePlansLoading}
            error={ratePlansError}
            currency={analysis.currency}
            propertyName={analysis.propertyName}
            analysisDate={analysis.analysisDate}
            lpcPromotionalDiscounts={lpcPromotionalDiscounts}
            promotionsLoading={promotionsLoading}
            promotionsError={promotionsError}
          />
        </Box>
      </Box>
    </Paper>
  );
}
